import React from 'react';
import { Briefcase } from 'lucide-react';
import { NotificationBlock, insertNotificationToken, wrapNotificationSelection } from '../PrestationPlanning/PrestationNotificationsPage';
import { GroupeNotifications } from '../ui/LigneNotification';

/** Les jetons disponibles dans les trois emails du devis. Volontairement courts : ce sont les seules
 *  informations dont on dispose à ce stade du parcours — un dossier n'a ni facture ni salarié. */
const JETONS_DEVIS = [
    { token: '{{nom}}', label: 'Nom (ou raison sociale) du client' },
    { token: '{{prenom}}', label: 'Prénom du client' },
    { token: '{{montant_devis}}', label: 'Montant TTC du devis' },
    { token: '{{entreprise}}', label: 'Votre établissement' },
    { token: '{{logo}}', label: 'Votre logo' },
];
const JETONS_ENVOI = [...JETONS_DEVIS, { token: '{{bouton}}', label: 'Bouton vers la page de signature' }];
const JETONS_REFUS = [...JETONS_DEVIS, { token: '{{motif}}', label: 'Motif indiqué par le client' }];

export interface DevisNotifications {
    /** Email d'envoi de la plaquette — le seul du parcours qui n'etait pas reglable jusqu'au 2026-08-29. */
    plaquette_send_subject: string;
    plaquette_send_body: string;
    /** Demarche vers laquelle le bouton renvoie par defaut : presque toujours la meme. */
    plaquette_default_template_id: string;
    devis_send_subject: string;
    devis_send_body: string;
    notify_devis_signed_received: boolean;
    devis_signed_received_subject: string;
    devis_signed_received_body: string;
    notify_devis_declined: boolean;
    devis_declined_subject: string;
    devis_declined_body: string;
}

/**
 * Un bloc email qui porte SES PROPRES références et son champ actif.
 *
 * Dans `ClientEmailNotificationsSettings`, chaque email traîne cinq déclarations à la main — deux refs, un
 * état de champ actif, une fonction d'insertion de jeton, une d'encadrement — recopiées quinze fois. Ce
 * n'est pas une faute de l'existant, c'est ce qui arrive quand un composant grandit ; mais le reproduire
 * ici ferait passer un fichier déjà au-dessus du plafond de 500 lignes à plus de 640, pour trois emails.
 * Ce petit emballage les garde à l'intérieur, et chaque bloc redevient une ligne.
 */
export function BlocEmail({ titre, description, bascule, sujet, setSujet, corps, setCorps, jetons }: {
    titre: string;
    /** Quand l'email part : sous le titre (ligne de Paramètres > Notifications, ou bloc complet). */
    description?: React.ReactNode;
    bascule?: { checked: boolean; onChange: (v: boolean) => void };
    sujet: string;
    setSujet: (v: string) => void;
    corps: string;
    setCorps: (v: string) => void;
    jetons: { token: string; label: string }[];
}) {
    const sujetRef = React.useRef<HTMLInputElement>(null);
    const corpsRef = React.useRef<HTMLTextAreaElement>(null);
    const [champActif, setChampActif] = React.useState<'subject' | 'body'>('body');

    return (
        <NotificationBlock
            title={titre}
            description={description}
            toggle={bascule}
            subject={sujet} setSubject={setSujet}
            body={corps} setBody={setCorps}
            subjectRef={sujetRef} bodyRef={corpsRef}
            activeField={champActif} setActiveField={setChampActif}
            onInsertToken={(t) => (champActif === 'subject'
                ? insertNotificationToken(sujetRef.current, sujet, setSujet, t)
                : insertNotificationToken(corpsRef.current, corps, setCorps, t))}
            onWrap={(m) => wrapNotificationSelection(corpsRef.current, corps, setCorps, m)}
            tokens={jetons}
        />
    );
}

/**
 * Section « Suivi privatisation » de l'onglet Notifications — les trois emails du devis et de son lien
 * public. N'apparaît que si le module est actif.
 *
 * Le mail ENVOYÉ au client n'a pas de bascule : l'envoyer est le geste même de l'étape « Envoyer le
 * devis », comme le mail de facture soldée. Les deux mails REÇUS par l'établissement en ont une —
 * quelqu'un qui vit dans le tableau de bord n'a pas forcément envie d'un email de plus.
 */
export function SuiviPrivatisationNotificationBlocks({ valeurs, onChange }: {
    valeurs: DevisNotifications;
    onChange: (patch: Partial<DevisNotifications>) => void;
}) {
    // La plaquette précède le devis dans le parcours : son email vient donc en premier.
    return (
        <GroupeNotifications titre="Suivi privatisation" icone={<Briefcase size={12} />}>
            <BlocEmail
                titre="Envoi de la plaquette"
                description="Repris à l'ouverture de la fenêtre d'envoi, où vous pouvez encore le retoucher pour un client. La plaquette part en pièce jointe."
                sujet={valeurs.plaquette_send_subject} setSujet={(v) => onChange({ plaquette_send_subject: v })}
                corps={valeurs.plaquette_send_body} setCorps={(v) => onChange({ plaquette_send_body: v })}
                jetons={JETONS_ENVOI} />
            <BlocEmail
                titre="Envoi du devis (avec le lien de signature)"
                description="Le devis part en pièce jointe. Sans variable « Bouton » dans le message, le bouton est ajouté à la fin."
                sujet={valeurs.devis_send_subject} setSujet={(v) => onChange({ devis_send_subject: v })}
                corps={valeurs.devis_send_body} setCorps={(v) => onChange({ devis_send_body: v })}
                jetons={JETONS_ENVOI} />
            <BlocEmail
                titre="Devis signé déposé (pour vous)"
                description="Vous prévient quand le client dépose son devis signé. Le dossier reste à « Devis envoyé » jusqu'à votre validation."
                bascule={{ checked: valeurs.notify_devis_signed_received, onChange: (v) => onChange({ notify_devis_signed_received: v }) }}
                sujet={valeurs.devis_signed_received_subject} setSujet={(v) => onChange({ devis_signed_received_subject: v })}
                corps={valeurs.devis_signed_received_body} setCorps={(v) => onChange({ devis_signed_received_body: v })}
                jetons={JETONS_DEVIS} />
            <BlocEmail
                titre="Le client ne donne pas suite (pour vous)"
                description="Le dossier passe en « Perdue » avec le motif indiqué, qui alimente votre bilan commercial."
                bascule={{ checked: valeurs.notify_devis_declined, onChange: (v) => onChange({ notify_devis_declined: v }) }}
                sujet={valeurs.devis_declined_subject} setSujet={(v) => onChange({ devis_declined_subject: v })}
                corps={valeurs.devis_declined_body} setCorps={(v) => onChange({ devis_declined_body: v })}
                jetons={JETONS_REFUS} />
        </GroupeNotifications>
    );
}
